import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as HyperlinkIcon } from '@/assets/icons/hyperlink.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import { PageHeading } from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useDebounce from '@/hooks/useDebounce';
import { api } from '@/utils/api';

import Blacklist, { Button } from './components/Blacklist';
import ProfanityCheckToggle from './components/ProfanityCheckToggle';

const Header = styled.div`
  display: flex;
  margin: 24px 0 12px;
  align-items: flex-end;
  justify-content: space-between;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
`;

const Badge = styled(Text)`
  display: flex;
  align-items: center;
  height: 18px;
  padding: 0 9px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.gray};
  margin-right: 12px;
`;

const PageName = styled.div`
  display: inline-flex;
  align-items: center;
`;

const PageThumb = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 100%;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  flex-shrink: 0;
  margin-right: 8px;
`;

const StyledTable = styled(Table)`
  td,
  th {
    &:first-child {
      font-weight: 700;
    }
  }
`;

const Tr = styled.tr`
  cursor: pointer;
  &:hover {
    > td {
      background: ${({ theme }) => theme.colors.grayscaleExtraLight};
    }
  }
`;

const Hyperlink = styled.a`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  svg {
    margin-right: 6px;
    width: 14px;
    flex-shrink: 0;
  }
  > span {
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
`;

export type DQCommentsCheckerPost = {
  readonly id: number;
  performed_at: string;
  is_deleting: boolean;
  malicious_comments?: Array<{
    id: string;
    message: string;
  }>;
  total_comments_count: number;
  malicious_comments_count: number;
  deleted_comments_count: number;
  post: {
    id: number;
    external_id: string;
    message?: string;
    published_at: string;
    channel: {
      id: number;
      name: string;
      media?: Array<{
        original_url: string;
      }>;
    };
  };
};

const CommentsChecker: FC = () => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const alerts = useAlerts();

  const debouncedSearch = useDebounce(search, 500);

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(
    [
      'dq_post_comment_checks',
      {
        search: debouncedSearch,
      },
    ],
    () =>
      api.get<
        never,
        { profanityCheckEnabled: boolean; checks: DQCommentsCheckerPost[] }
      >('/v1/editorial/comment-checks', {
        params: {
          ...(!!debouncedSearch && {
            search: debouncedSearch,
          }),
        },
      }),
    {
      refetchInterval: 15_000,
    },
  );

  const onClear = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        'All checks will be deleted',
      );

      if (!confirmed) {
        return;
      }

      await api.post('/v1/editorial/comment-checks/clear');

      await queryClient.invalidateQueries(['dq_post_comment_checks']);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <PageHeading>Comments Checker</PageHeading>
      <SearchBar
        placeholder="Search by Post ID"
        value={search}
        setValue={setSearch}
      />
      <Header>
        <Heading $color="gray" $type="smallHeadline" as="div">
          <Badge as="div" $type="extraSmall" $color="white">
            {data?.checks?.length ?? '-'}
          </Badge>
          Checks list
        </Heading>
        <Actions>
          <ProfanityCheckToggle enabled={data?.profanityCheckEnabled} />
          <Button onClick={onClear}>
            <TrashIcon />
            <Text as="span" $type="smallBold">
              Clear
            </Text>
          </Button>
          <Blacklist />
          <Blacklist whitelist />
        </Actions>
      </Header>
      <StyledTable $fixed>
        <thead>
          <tr>
            <Th>Page</Th>
            <Th>Post</Th>
            <Th>Checked At</Th>
            <Th>Comments Checked</Th>
            <Th>Malicious Comments</Th>
            <Th>Deleted Comments</Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            colSpan={6}
            isLoading={isLoading}
            isError={isError}
            hasData={!!data?.checks?.length}
          >
            {data?.checks?.map((check) => (
              <Tr
                key={check.id}
                onClick={() => navigate(`./${check.id}`, { relative: 'path' })}
              >
                <Td>
                  <PageName>
                    {!!check.post.channel.media?.[0]?.original_url && (
                      <PageThumb
                        loading="lazy"
                        src={check.post.channel.media[0].original_url}
                        alt=""
                      />
                    )}
                    {check.post.channel.name}
                  </PageName>
                </Td>
                <Td>
                  <Hyperlink
                    onClick={(e) => e.stopPropagation()}
                    title={check.post.external_id}
                    target="_blank"
                    rel="noreferrer noopener"
                    href={`https://facebook.com/${check.post.external_id}`}
                  >
                    <HyperlinkIcon />
                    <span>{check.post.external_id}</span>
                  </Hyperlink>
                </Td>
                <Td>
                  {DateTime.fromISO(check.performed_at).toFormat(
                    'yyyy/LL/dd   HH:mm',
                  )}
                </Td>
                <Td>{check.total_comments_count}</Td>
                <Td>{check.malicious_comments_count}</Td>
                <Td>{check.deleted_comments_count}</Td>
              </Tr>
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
    </>
  );
};

export default CommentsChecker;
